import React from 'react';
import { SiteSwitch } from './SiteSwitch';
import { EventSelector } from './EventSelector';
import { AboutMenu, getAboutLinks } from './AboutMenu';
import { HowToUse } from './HowToUse';
import { getTwemojiUrl, LanguageSelector, languages } from './LanguageSelector';
import { useTranslation } from '../libs/common/i18n';
import { getSiteConfig } from '../libs/common/siteConfig';
import { getThemeColors, ThemePresets, setTheme, getCurrentTheme } from '../libs/common/themeConfig';
import {
  Bars3Icon,
  InformationCircleIcon,
  MoonIcon,
  QuestionMarkCircleIcon,
  SunIcon,
} from '@heroicons/react/24/outline';

const THEME_CYCLE = [ThemePresets.LIGHT, ThemePresets.DARK];

function ThemeCycleButton({ themeColors }) {
  const current = getCurrentTheme();
  const effective = current === ThemePresets.LIGHT ? ThemePresets.LIGHT : ThemePresets.DARK;

  const Icon = effective === ThemePresets.LIGHT ? SunIcon
    : MoonIcon;

  const next = THEME_CYCLE[(THEME_CYCLE.indexOf(effective) + 1) % THEME_CYCLE.length];
  const label = effective === ThemePresets.LIGHT ? 'Light'
    : 'Dark';

  return (
    <button
      onClick={() => { setTheme(next); window.location.reload(); }}
      className="p-2 rounded transition-colors min-h-[2.5rem] min-w-[2.5rem] touch-manipulation flex items-center justify-center hover:bg-[var(--hover-bg)]"
      style={{
        color: themeColors.text,
        backgroundColor: 'transparent',
        border: `1px solid ${themeColors.border}`,
        '--hover-bg': themeColors.hover,
      }}
      title={`Theme: ${label}`}
    >
      <Icon style={{ width: '1.125rem', height: '1.125rem' }} />
    </button>
  );
}

function MobileUtilityMenu({ themeColors }) {
  const { t, language, setLanguage } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isHelpOpen, setIsHelpOpen] = React.useState(false);
  const current = getCurrentTheme();
  const effective = current === ThemePresets.LIGHT ? ThemePresets.LIGHT : ThemePresets.DARK;
  const nextTheme = THEME_CYCLE[(THEME_CYCLE.indexOf(effective) + 1) % THEME_CYCLE.length];
  const ThemeIcon = effective === ThemePresets.LIGHT ? SunIcon : MoonIcon;
  const aboutLinks = getAboutLinks();

  const menuItemStyle = {
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${themeColors.border}`,
    color: themeColors.text,
    '--hover-bg': themeColors.hover,
  };

  const closeMenu = () => setIsOpen(false);

  return (
    <div className="topbar-mobile-utility relative flex-shrink-0 order-3">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded transition-colors min-h-[2.5rem] min-w-[2.5rem] touch-manipulation flex items-center justify-center hover:bg-[var(--hover-bg)]"
        style={{
          color: themeColors.text,
          backgroundColor: 'transparent',
          border: `1px solid ${themeColors.border}`,
          '--hover-bg': themeColors.hover,
        }}
        title="Menu"
      >
        <Bars3Icon style={{ width: '1.125rem', height: '1.125rem' }} />
      </button>

      {isOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 49 }}
            onClick={closeMenu}
          />
          <div
            className="topbar-mobile-menu"
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '0.5rem',
              width: 'min(20rem, calc(100vw - 1rem))',
              maxHeight: 'calc(100vh - 5rem)',
              overflowY: 'auto',
              backgroundColor: themeColors.surface,
              border: `1px solid ${themeColors.border}`,
              borderRadius: '0.5rem',
              boxShadow: '0 1.25rem 3rem rgba(0, 0, 0, 0.3)',
              zIndex: 60,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <label
              className="w-full py-3 px-4 text-left flex items-center gap-3 border-b min-h-[3.5rem]"
              style={menuItemStyle}
            >
              <img
                src={getTwemojiUrl((languages.find((lang) => lang.code === language) || languages[0]).emoji)}
                alt=""
                aria-hidden="true"
                style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0 }}
              />
              <span style={{ flex: 1, minWidth: 0, fontSize: '0.875rem', fontWeight: 600 }}>
                Language
              </span>
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                className="rounded px-2 py-1 text-sm"
                style={{
                  maxWidth: '9rem',
                  color: themeColors.text,
                  backgroundColor: themeColors.background,
                  border: `1px solid ${themeColors.border}`,
                }}
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </label>

            <button
              onClick={() => { setTheme(nextTheme); window.location.reload(); }}
              className="w-full py-3 px-4 text-left transition-colors flex items-center gap-3 border-b min-h-[3.5rem] touch-manipulation hover:bg-[var(--hover-bg)]"
              style={menuItemStyle}
            >
              <ThemeIcon style={{ width: '1.25rem', height: '1.25rem', color: themeColors.textSecondary, flexShrink: 0 }} />
              <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                {t('themeMode')}: {effective === ThemePresets.LIGHT ? t('lightTheme') : t('darkTheme')}
              </span>
            </button>

            <button
              onClick={() => { setIsHelpOpen(true); closeMenu(); }}
              className="w-full py-3 px-4 text-left transition-colors flex items-center gap-3 border-b min-h-[3.5rem] touch-manipulation hover:bg-[var(--hover-bg)]"
              style={menuItemStyle}
            >
              <QuestionMarkCircleIcon style={{ width: '1.25rem', height: '1.25rem', color: themeColors.textSecondary, flexShrink: 0 }} />
              <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{t('howToUse')}</span>
            </button>

            <div
              className="py-2 px-4 border-b"
              style={{
                borderBottomColor: themeColors.border,
                color: themeColors.textSecondary,
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {t('about')}
            </div>
            {aboutLinks.map((link) => {
              const Icon = link.icon || InformationCircleIcon;
              return (
                <button
                  key={link.path}
                  onClick={() => {
                    if (link.external) window.open(link.path, '_blank');
                    else window.location.href = link.path;
                    closeMenu();
                  }}
                  className="w-full py-3 px-4 text-left transition-colors flex items-center gap-3 border-b min-h-[3.25rem] touch-manipulation hover:bg-[var(--hover-bg)]"
                  style={menuItemStyle}
                >
                  <Icon style={{ width: '1.2rem', height: '1.2rem', color: themeColors.textSecondary, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: '0.875rem', fontWeight: 500 }}>
                    {t(link.titleKey)}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
      <HowToUse
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        renderTrigger={() => null}
      />
    </div>
  );
}

/**
 * Top navbar with site switch and event selector
 * Must be rendered inside Router context
 */
export const TopBar = () => {
  const siteConfig = getSiteConfig();
  const themeColors = getThemeColors();

  return (
    <div
      className="sticky top-0 w-full border-b backdrop-blur-md bg-opacity-95 z-50 transition-all duration-300"
      style={{
        borderColor: themeColors.border,
        backgroundColor: themeColors.surface,
      }}
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8">
        <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 py-2 sm:py-3 flex-wrap">
          <SiteSwitch />
          <a
            href="/"
            className="flex items-center gap-2 no-underline transition-opacity hover:opacity-80 flex-shrink-0"
            style={{ color: themeColors.text }}
          >
            <span className="hidden md:inline whitespace-nowrap text-base lg:text-xl font-bold">
              Majors.im
            </span>
          </a>
          <div className="flex items-center gap-2 flex-1 min-w-0 order-2 sm:order-none">
            <div className="flex-1 min-w-0">
              <EventSelector />
            </div>
          </div>
          <div className="topbar-desktop-utilities items-center gap-2 flex-shrink-0 order-3">
            <LanguageSelector />
            <ThemeCycleButton themeColors={themeColors} />
            <HowToUse />
            <AboutMenu />
          </div>
          <MobileUtilityMenu themeColors={themeColors} />
        </div>
      </div>
    </div>
  );
};
